import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useI18n } from "../i18n/I18nProvider";
import { PageHeader } from "../components/ui/PageHeader";
import { ErrorText } from "../components/ui/feedback";
import { Board, QueryError, QuotaBar, StatusLabel, type MarkTone } from "./dense";
import { formatBytes } from "./format";
import { ConfigurationValidation } from "./ConfigurationValidation";
import { TakedownForm } from "./TakedownForm";
import {
  adminWorkspaceConfigurationsQuery,
  adminWorkspaceMembersQuery,
  adminWorkspaceQuery,
} from "./queries";
import { workspaceStatus } from "./workspace-status";
import type { WorkspaceStatus } from "./types";

export interface WorkspaceDetailPageProps {
  workspaceId: string;
}

const MARK: Record<WorkspaceStatus, MarkTone> = {
  active: "blue",
  suspended: "red",
  pending: "hollow",
  deleted: "muted",
};

/** One workspace for an admin: usage, members, configurations (validate each) and media takedown. */
export function WorkspaceDetailPage({ workspaceId }: WorkspaceDetailPageProps) {
  const { t } = useI18n();
  const workspace = useQuery(adminWorkspaceQuery(workspaceId));
  const configurations = useQuery(adminWorkspaceConfigurationsQuery(workspaceId));
  const members = useQuery(adminWorkspaceMembersQuery(workspaceId));
  const data = workspace.data;
  const status = data ? workspaceStatus(data) : null;

  return (
    <div className="space-y-6">
      <Link
        to="/admin"
        className="inline-flex min-h-11 items-center text-note font-semibold text-blue hover:text-blue-deep"
      >
        {t("admin.workspace.back")}
      </Link>
      {workspace.isError ? (
        <QueryError error={workspace.error} onRetry={() => void workspace.refetch()} />
      ) : null}
      {data && status ? (
        <PageHeader
          title={data.name}
          actions={
            <>
              <StatusLabel tone={MARK[status]}>{t(`admin.status.${status}`)}</StatusLabel>
              <span className="text-note text-ink-3">{data.plan}</span>
            </>
          }
        />
      ) : null}

      <div className="grid gap-5 lg:grid-cols-2">
        {data ? (
          <Board title={t("admin.usage.title")}>
            <div className="space-y-4">
              <div>
                <p className="mb-1 text-note font-semibold text-ink-2">
                  {t("admin.usage.players")}
                </p>
                <QuotaBar
                  className="w-full"
                  fraction={data.quotaPersons > 0 ? data.persons / data.quotaPersons : 0}
                  label={`${data.persons} / ${data.quotaPersons}`}
                />
              </div>
              <div>
                <p className="mb-1 text-note font-semibold text-ink-2">{t("admin.col.storage")}</p>
                <QuotaBar
                  className="w-full"
                  fraction={data.quotaBytes > 0 ? data.bytesUsed / data.quotaBytes : 0}
                  label={t("admin.quota", {
                    used: formatBytes(data.bytesUsed),
                    quota: formatBytes(data.quotaBytes),
                  })}
                />
              </div>
            </div>
          </Board>
        ) : null}

        <Board title={t("admin.col.members")}>
          {members.isError ? (
            <QueryError error={members.error} onRetry={() => void members.refetch()} />
          ) : null}
          {members.isPending ? <p className="text-operate text-ink-3">{t("app.loading")}</p> : null}
          {members.data && members.data.length === 0 ? (
            <p className="text-operate text-ink-3">{t("admin.workspace.noMembers")}</p>
          ) : null}
          {members.data && members.data.length > 0 ? (
            <ul className="divide-y divide-hairline">
              {members.data.map((member) => (
                <li
                  key={member.operatorId}
                  className="flex items-center justify-between gap-3 py-2.5 text-operate"
                >
                  <span className="min-w-0 truncate">
                    {member.name || member.email}
                    <span className="text-note text-ink-3"> · {member.email}</span>
                  </span>
                  <span className="shrink-0 text-note font-semibold text-ink-2">{member.role}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </Board>
      </div>

      <Board title={t("admin.workspace.configurations")}>
        <ErrorText error={configurations.error} />
        {configurations.data && configurations.data.length === 0 ? (
          <p className="text-operate text-ink-3">{t("admin.workspace.noConfigurations")}</p>
        ) : null}
        <ul className="divide-y divide-hairline">
          {(configurations.data ?? []).map((configuration) => (
            <ConfigurationValidation
              key={configuration.id}
              workspaceId={workspaceId}
              configuration={configuration}
            />
          ))}
        </ul>
      </Board>

      <TakedownForm />
    </div>
  );
}
